import React, { useEffect, useRef } from 'react';

/**
 * Idle Logoff Modal - Custom implementation without Radix
 * 
 * IMPORTANT: This uses a custom modal instead of Radix Dialog because
 * Radix Dialog was intercepting button click events, causing the Log Out
 * button to not work properly.
 */
const IdleWarningModal = ({ 
  isOpen, 
  remainingTime,
  secondsRemaining,
  countdown,
  formattedTime,
  idleMinutes,
  onStayLoggedIn, 
  onLogout 
}) => {
  const modalRef = useRef(null);
  const logoutButtonRef = useRef(null);
  
  // Use countdown if provided (from IdleManager), otherwise fall back to other props
  const effectiveSeconds = countdown || secondsRemaining || 120;
  
  // Use formattedTime if provided, otherwise calculate from seconds
  const displayTime = formattedTime || 
    `${Math.floor(effectiveSeconds / 60)}:${(effectiveSeconds % 60).toString().padStart(2, '0')}`;
  
  const totalSeconds = effectiveSeconds;
  // Jun 2026 — minutes idle comes from the configured warning threshold
  // (passed by IdleManager) instead of a hardcoded 15-min/2-min
  // assumption that was wrong for any other configured timeout.
  const minutesIdle = Math.max(1, idleMinutes ?? 13);
  const isUrgent = totalSeconds <= 30;

  // Handle escape key (prevent closing)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle logout click with multiple fallback strategies
  const handleLogoutClick = (e) => {
    console.log('=== LOGOUT BUTTON CLICKED ===');
    console.log('Event type:', e.type);
    console.log('Event target:', e.target);
    console.log('onLogout function:', typeof onLogout);
    
    // Prevent any default behavior
    if (e.preventDefault) e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    
    // Call the logout handler
    if (onLogout && typeof onLogout === 'function') {
      console.log('Calling onLogout...');
      try {
        onLogout();
        console.log('onLogout called successfully');
      } catch (error) {
        console.error('Error calling onLogout:', error);
        // Fallback: Force logout
        forceLogout();
      }
    } else {
      console.error('onLogout is not a function, using fallback');
      forceLogout();
    }
  };
  
  // Fallback logout function
  const forceLogout = () => {
    console.log('=== FORCE LOGOUT TRIGGERED ===');
    try {
      // Clear all auth data
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      sessionStorage.clear();

      // Feb 2026 unified logout notice (force-logout fallback path).
      try {
        // Inline require to avoid circular import risk in this
        // last-resort path.
        const { queueLogoutNotice } = require('../utils/logoutNotice');
        queueLogoutNotice('manual');
      } catch (_) { /* helper unavailable — silent */ }

      console.log('Auth data cleared, redirecting to /login');
      
      // Force redirect
      window.location.replace('/login');
    } catch (error) {
      console.error('Force logout error:', error);
      // Last resort
      window.location.replace('/login');
    }
  };

  // Handle stay logged in click
  const handleStayLoggedInClick = (e) => {
    console.log('=== STAY LOGGED IN BUTTON CLICKED ===');
    if (e.preventDefault) e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    
    if (onStayLoggedIn && typeof onStayLoggedIn === 'function') {
      onStayLoggedIn();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9998] bg-black/80"
        aria-hidden="true"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      
      {/* Modal */}
      <div 
        role="dialog"
        aria-modal="true"
        aria-labelledby="idle-modal-title"
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        onClick={(e) => {
          // Prevent clicks on backdrop from doing anything
          if (e.target === e.currentTarget) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div 
          ref={modalRef}
          data-idle-modal="true"
          className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-center justify-center py-2 px-4 text-center">
            {/* Large circular warning icon with timer */}
            <div className="mb-6">
              <div className={`relative w-24 h-24 rounded-full border-4 ${isUrgent ? 'border-red-500' : 'border-orange-400'} flex flex-col items-center justify-center transition-colors duration-300`}>
                <svg 
                  className={`w-8 h-8 ${isUrgent ? 'text-red-500' : 'text-orange-400'} mb-1 transition-colors duration-300`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2.5} 
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
                <span className={`text-lg font-bold ${isUrgent ? 'text-red-500' : 'text-orange-500'} transition-colors duration-300`}>
                  {displayTime}
                </span>
              </div>
            </div>

            {/* Heading */}
            <h2 id="idle-modal-title" className="text-2xl font-bold text-gray-900 mb-3">
              {isUrgent ? 'Logging out soon!' : 'Are you still there?'}
            </h2>

            {/* Body text */}
            <p className="text-base text-gray-600 mb-4 max-w-sm leading-relaxed">
              You have been idle for {minutesIdle > 0 ? minutesIdle : 'several'} minute{minutesIdle !== 1 ? 's' : ''} and will be logged out in <strong className={isUrgent ? 'text-red-600' : 'text-orange-600'}>{displayTime}</strong>.
            </p>
            
            {/* Security note */}
            <p className="text-sm text-gray-500 mb-6 max-w-sm">
              For security, inactive sessions are automatically logged out.
            </p>

            {/* Action Buttons - Using native buttons with direct handlers.
                Feb 2026 P0 (IDLE-STAY-DOUBLE-FIRE) — removed the
                duplicate `onMouseDown` handler that previously fired
                the same callback once on mousedown AND once on click,
                spawning two concurrent /api/session/activity calls per
                user click. The double dispatch races at the backend
                (Redis activity-ping commit) and surfaces as a false
                "Couldn't reach the server to extend the session" toast
                from whichever of the two races returns a non-200. */}
            <div className="flex gap-4 w-full justify-center">
              <button 
                ref={logoutButtonRef}
                type="button"
                onClick={handleLogoutClick}
                className="px-8 py-2.5 border-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400 font-medium rounded-lg text-sm inline-flex items-center justify-center cursor-pointer transition-colors active:bg-gray-200"
                data-testid="idle-modal-logout-btn"
                style={{ touchAction: 'manipulation' }}
              >
                Log Out
              </button>
              <button 
                type="button"
                onClick={handleStayLoggedInClick}
                className={`px-8 py-2.5 ${isUrgent ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'} text-white font-medium transition-colors duration-300 rounded-lg text-sm inline-flex items-center justify-center cursor-pointer active:opacity-80`}
                autoFocus
                data-testid="idle-modal-stay-btn"
                style={{ touchAction: 'manipulation' }}
              >
                Stay Signed In
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IdleWarningModal;
